import { XCircleIcon } from "@heroicons/react/16/solid";
import { useContactModalStore } from "../../stores/contactModalStore";
import { motion } from "framer-motion";
import { useState } from "react";
import { ButtonGreen } from "./ButtonGreen";
import { useWindowSize } from "@uidotdev/usehooks";

export const ContactModal = () => {
    const { showContactModal, hideContactModal, isShownContactModal } =
        useContactModalStore();

    // States of form
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [company, setCompany] = useState("");
    const [notes, setNotes] = useState("");

    // States of error
    const [nameError, setNameError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [companyError, setCompanyError] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const [responseMessage, setResponseMessage] = useState("");

    const sendMessage = () => {
        // handle errors
        if (
            !name ||
            name.length < 1 ||
            !email ||
            email.length < 1 ||
            !company ||
            company.length < 1
        ) {
            if (!name || name.length < 1) {
                setNameError(true);
            }
            if (!email || email.length < 1) {
                setEmailError(true);
            }
            if (!company || company.length < 1) {
                setCompanyError(true);
            }
            return;
        }
        // all good to send message

        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
            setResponseMessage("Oops, there was an error.");
        }, 2000);
    };

    const sizes = useWindowSize();

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                if (
                    e &&
                    e.target &&
                    (e.target as HTMLElement).id ===
                        "contact-modal-close-background"
                ) {
                    hideContactModal();
                }
            }}
            id="contact-modal-close-background"
            className="fixed z-[9999] w-full h-[100vh] bg-black bg-opacity-20 backdrop-blur-sm"
        >
            <motion.div className="flex flex-col bg-black-pro shadow-sm pt-[20px] pb-[40px] px-[40px] h-[95vh] w-[90vw] max-w-[900px] fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-[20px]">
                <div>
                    <XCircleIcon
                        onClick={() => {
                            if (!isShownContactModal) {
                                showContactModal();
                            }
                            if (isShownContactModal) {
                                hideContactModal();
                            }
                        }}
                        className="w-[20px] h-[20px] text-white cursor-pointer float-end"
                    />
                </div>
                <div className="flex flex-col justify-between h-full">
                    <div className="flex flex-col gap-[10px] md:gap-[20px]">
                        <div className="flex flex-col gap-[4px] md:gap-[8px]">
                            <p className="text-[20px] md:text-[36px] font-helvetica-medium text-white">
                                Let's Talk
                            </p>
                            <p className="text-[16px] md:text-[20px] font-helvetica-regular text-white">
                                Contact us to learn more about our program,
                                application process, and how we can help you
                                achieve your business goals with AI.
                            </p>
                        </div>
                        <div className="flex flex-col gap-[4px] md:gap-[8px] justify-start">
                            {sizes && sizes.height && sizes.height > 635 && (
                                <div className="flex items-center gap-[4px] md:gap-[8px]">
                                    <p className="text-green-pro">•</p>
                                    <p className="text-gray-pro text-[14px] md:text-[16px] font-helvetica-regular">
                                        We will respond as soon as possible
                                    </p>
                                </div>
                            )}

                            <div className="flex items-center gap-[4px] md:gap-[8px]">
                                <p className="text-green-pro">•</p>
                                <p className="text-gray-pro text-[14px] md:text-[16px] font-helvetica-regular">
                                    We're happy to sign a NDA if requested
                                </p>
                            </div>
                            <div className="flex items-center gap-[4px] md:gap-[8px]">
                                <p className="text-green-pro">•</p>
                                <p className="text-gray-pro text-[14px] md:text-[16px] font-helvetica-regular">
                                    You can email us at{" "}
                                    <span className="text-green-pro">
                                        contact@anserini.com
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-[10px] md:gap-[20px]">
                        <div className="flex flex-col">
                            <p className="text-[16px] md:text-[20px] font-helvetica-regular text-white">
                                Your Name
                            </p>
                            <input
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (
                                        e.target.value &&
                                        e.target.value.length > 0
                                    ) {
                                        setNameError(false);
                                    }
                                }}
                                type="text"
                                className={`border-b ${
                                    nameError
                                        ? "border-red-600"
                                        : "border-white"
                                } px-2  bg-transparent text-white font-helvetica-regular text-[14px] md:text-[16px] placeholder-white focus:outline-none focus:border-white`}
                            />
                        </div>
                        <div className="flex flex-col">
                            <p className="text-[16px] md:text-[20px]  font-helvetica-regular text-white">
                                Your Email
                            </p>
                            <input
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (
                                        e.target.value &&
                                        e.target.value.length > 0
                                    ) {
                                        setEmailError(false);
                                    }
                                }}
                                type="email"
                                className={` border-b px-2 ${
                                    emailError
                                        ? "border-red-600"
                                        : "border-white"
                                }  bg-transparent text-white font-helvetica-regular text-[14px] md:text-[16px] placeholder-white focus:outline-none focus:border-white`}
                            />
                        </div>
                        <div className="flex flex-col">
                            <p className="text-[16px] md:text-[20px]  font-helvetica-regular text-white">
                                Company Name
                            </p>
                            <input
                                value={company}
                                onChange={(e) => {
                                    setCompany(e.target.value);
                                    if (
                                        e.target.value &&
                                        e.target.value.length > 0
                                    ) {
                                        setCompanyError(false);
                                    }
                                }}
                                type="text"
                                className={`border-b ${
                                    companyError
                                        ? "border-red-600"
                                        : "border-white"
                                } px-2 bg-transparent text-white font-helvetica-regular text-[14px] md:text-[16px] placeholder-white focus:outline-none focus:border-white`}
                            />
                        </div>
                        <div className="flex flex-col">
                            <p className="text-[16px] md:text-[20px]  font-helvetica-regular text-white">
                                Notes
                            </p>
                            <input
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                type="text"
                                className="border-b px-2 border-white bg-transparent text-white font-helvetica-regular text-[16px] placeholder-white focus:outline-none focus:border-white"
                            />
                        </div>
                        <div
                            onClick={(e) => {
                                const id = (e.target as HTMLDivElement).id;
                                if (
                                    id &&
                                    [
                                        "bold",
                                        "button-container",
                                        "main-button",
                                        "fonts-abs",
                                        "icon-container",
                                        "icon",
                                        "ArrowRightIcon",
                                    ].includes(id)
                                )
                                    sendMessage();
                            }}
                            className="flex justify-end"
                        >
                            {isLoading ? (
                                <LoadingIcon />
                            ) : (
                                <p className="text-[16px] md:text-[16px] font-helvetica-regular text-red-600">
                                    {responseMessage}
                                </p>
                            )}
                            {!isLoading && !responseMessage && (
                                <ButtonGreen title="Send Message" />
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

const LoadingIcon = () => {
    return (
        <div role="status">
            <svg
                aria-hidden="true"
                className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-[#D0FF71]"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                />
                <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                />
            </svg>
            <span className="sr-only">Loading...</span>
        </div>
    );
};
